import BareScreen from "@components/General/BareScreen";
import styles from "@styles/screens/HourBankScreen.module.css";
import { Organization } from "@models/Organization";
import SearchBar from "@components/General/SearchBar";
import { BigRefreshButton, NormalButton } from "@components/General/Button";
import { useEffect, useMemo, useState } from "react";
import { HourBankSummary } from "@services/HourBankService";
import { Application } from "@core/Application";
import UserCard from "@components/General/UserCard";
import { CalendarMonthOutlined, VisibilityOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts";
import { DatePickerPopperWithRange } from "@components/General/TablePoppers";
import { GridTable, GridTableCell, GridTableRow, PaginatedGridTable, TableColumn } from "@shared/designSystem/Tables";
import { HourBankCompensationRequest } from "@services/HourBankCompensationService";
import { usePopup } from "@contexts/popupContext";
import { Modal } from "@shared/designSystem/messages/Modal";
import { TextInput } from "@components/General/TextInput";
import ResultPopup from "@components/General/ResultPopup";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { HollowedButton } from "@shared/designSystem/Button";
import { HorizontalContainer, SimpleCardContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { BoldText, Text } from "@shared/designSystem/Texts";
import { Loader } from "@shared/designSystem/Loader";

const BankSummary = ({summary, loading}: {summary: HourBankSummary, loading: boolean}) => {
    const entries = useMemo(() => Object.values(summary || {}), [summary]);
    const totals = entries.reduce((acc, entry) => {
        acc.minutesWorked += entry.minutesWorked;
        acc.minutesExpected += entry.minutesExpected;
        acc.minutesDifference += entry.minutesDifference;
        return acc;
    }, { minutesWorked: 0, minutesExpected: 0, minutesDifference: 0 });

    const otherMinutes = Math.max(
        0,
        Math.abs(totals.minutesDifference - (totals.minutesWorked - totals.minutesExpected))
    );

    const chartData = entries.length
        ? [
            { id: 0, label: "Trabajadas", value: totals.minutesWorked, color: "#1c8b3c" },
            { id: 1, label: "Esperadas", value: totals.minutesExpected, color: "#4f6bf0" },
            ...(otherMinutes > 0
                ? [{ id: 2, label: "Otras", value: otherMinutes, color: "#f2a33b" }]
                : [])
        ].filter((item) => item.value > 0)
        : [];

    const differenceClass =
        totals.minutesDifference > 0
            ? styles["balance-positive"]
            : totals.minutesDifference < 0
                ? styles["balance-negative"]
                : styles["balance-neutral"];

    return (
        <SimpleCardContainer className={styles["bank-summary-card"]}>
            <div className={styles["bank-summary-content"]}>
                <div className={styles["bank-summary-chart"]}>
                    {loading ? (
                        <div className={styles["bank-summary-loading"]}>
                            <Loader />
                        </div>
                    ) : entries.length ? (
                        <PieChart
                            series={[
                                {
                                    data: chartData,
                                    innerRadius: 48,
                                    outerRadius: 88,
                                    paddingAngle: 2,
                                    cornerRadius: 4
                                }
                            ]}
                            height={220}
                            margin={{ right: 0, left: 0, top: 0, bottom: 0 }}
                            hideLegend
                            sx={{
                                [`& .${pieArcLabelClasses.root}`]: {
                                    fill: "#ffffff",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    fontFamily: "Inter, sans-serif"
                                }
                            }}
                        />
                    ) : (
                        <div className={styles["bank-summary-empty"]}>Sin datos</div>
                    )}
                </div>
                <div className={styles["bank-summary-stats"]}>
                    <div className={styles["bank-summary-title"]}>Resumen general</div>
                        <div className={styles["bank-summary-row"]}>
                            <span className={styles["bank-summary-label"]}>
                                <span
                                    className={styles["bank-summary-dot"]}
                                    style={{ background: "#1c8b3c" }}
                                />
                                Total trabajadas
                            </span>
                            <strong>{minutesToHoursMinutes(totals.minutesWorked)}</strong>
                        </div>
                        <div className={styles["bank-summary-row"]}>
                            <span className={styles["bank-summary-label"]}>
                                <span
                                    className={styles["bank-summary-dot"]}
                                    style={{ background: "#4f6bf0" }}
                                />
                                Total esperadas
                            </span>
                            <strong>{minutesToHoursMinutes(totals.minutesExpected)}</strong>
                        </div>
                        {otherMinutes > 0 && (
                            <div className={styles["bank-summary-row"]}>
                                <span className={styles["bank-summary-label"]}>
                                    <span
                                        className={styles["bank-summary-dot"]}
                                        style={{ background: "#f2a33b" }}
                                    />
                                    Otras horas
                                </span>
                                <strong>{minutesToHoursMinutes(otherMinutes)}</strong>
                            </div>
                        )}
                    <div className={styles["bank-summary-row"]}>
                        <span>Diferencia total</span>
                        <strong className={differenceClass}>
                            {totals.minutesDifference > 0 ? "+" : totals.minutesDifference < 0 ? "-" : ""}
                            {minutesToHoursMinutes(Math.abs(totals.minutesDifference))}
                        </strong>
                    </div>
                </div>
            </div>
        </SimpleCardContainer>
    );
}

function minutesToHoursMinutes(minutes: number): string {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
}

interface HourBankSummaryEntry {
    name: string;
    image?: string;
    minutesWorked: number;
    minutesExpected: number;
    minutesDifference: number;
}

interface HourBankTableRow {
    userID: string;
    entry: HourBankSummaryEntry;
}

interface CompensationReviewPopupProps {
    request: CompensationRequestRow;
    action: "approve" | "reject";
    onSubmit: (request: CompensationRequestRow, description: string) => Promise<void>;
}

interface CompensationRequestRow {
    id: string;
    userID: string;
    collaboratorName: string;
    day: string;
    requestedMinutes: number;
    balanceMinutesAtRequest: number;
    status: HourBankCompensationRequest["status"];
    createdAt: number;
    updatedAt: number;
    reason: string;
}

const compensationStatusLabel: Record<HourBankCompensationRequest["status"], string> = {
    PENDING: "Pendiente",
    APPROVED: "Aprobada",
    REJECTED: "Rechazada",
    CANCELLED: "Cancelada",
};

const formatDateFilterLabel = (
    inputDateMethod: string,
    date: Date | null,
    dateRange: [Date, Date] | null,
    timezone: string,
) => {
    if (inputDateMethod === "Unica" && date) {
        return `Resumen para ${date.toLocaleDateString("es-AR", { timeZone: timezone })}`;
    }

    if (inputDateMethod === "Rango" && dateRange) {
        const startDate = dateRange[0].toLocaleDateString("es-AR", { timeZone: timezone });
        const endDate = dateRange[1].toLocaleDateString("es-AR", { timeZone: timezone });
        return `Resumen del ${startDate} al ${endDate}`;
    }

    return "Resumen general";
};

const CompensationReviewPopup: React.FC<CompensationReviewPopupProps> = ({
    request,
    action,
    onSubmit,
}) => {
    const { hidePopup, showPopup } = usePopup();
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);

    const isApprove = action === "approve";
    const actionLabel = isApprove ? "aprobar" : "rechazar";

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await onSubmit(request, description.trim());
            showPopup(() => (
                <ResultPopup
                    result={`Solicitud ${isApprove ? "aprobada" : "rechazada"} correctamente`.localized()}
                    succesful={true}
                />
            ));
        } catch (error) {
            console.error(error);
            showPopup(() => (
                <ResultPopup
                    result={`Error al ${actionLabel} la solicitud`.localized()}
                    succesful={false}
                />
            ));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={hidePopup}
            title={`${isApprove ? "Aprobar" : "Rechazar"} solicitud`.localized()}
            maxWidth="520px"
        >
            <VerticalContainer gap={designSystemStyles.gapSm} alignment="left">
                <Text text={`Solicitud de ${request.collaboratorName} para ${minutesToHoursMinutes(request.requestedMinutes)} el día ${request.day}`.localized()} />
                <TextInput
                    label={"Comentario opcional".localized()}
                    placeholder={"Agregar detalle para la resolución".localized()}
                    value={description}
                    onChange={setDescription}
                    loading={loading}
                />
                <HorizontalContainer justifyContent={"space-between"} width="100%" gap={designSystemStyles.gapSm}>
                    <div style={{ width: "120px", height: "50px" }}>
                        <HollowedButton onClick={hidePopup} text={"Cancelar".localized()} disabled={loading} />
                    </div>
                    <div style={{ width: "120px", height: "50px" }}>
                        <NormalButton
                            onClick={handleSubmit}
                            text={`${isApprove ? "Aprobar" : "Rechazar"}`.localized()}
                            loading={loading}
                        />
                    </div>
                </HorizontalContainer>
            </VerticalContainer>
        </Modal>
    );
};

const CompensationRequestsPanel = ({
    requests,
    loading,
    onApprove,
    onReject,
}: {
    requests: CompensationRequestRow[];
    loading: boolean;
    onApprove: (request: CompensationRequestRow) => void;
    onReject: (request: CompensationRequestRow) => void;
}) => {
    const tableColumns: TableColumn[] = useMemo(() => ([
        { id: "collaborator", label: "Colaborador", width: "1.2fr" },
        { id: "date", label: "Fecha", width: "0.9fr" },
        { id: "minutes", label: "Compensación", width: "0.9fr", align: "center" },
        { id: "status", label: "Estado", width: "0.9fr", align: "center" },
        { id: "actions", label: "", width: "170px", align: "right" },
    ]), []);

    const getStatusClassName = (status: CompensationRequestRow["status"]) => {
        switch (status) {
            case "APPROVED":
                return styles["status-approved"];
            case "REJECTED":
                return styles["status-rejected"];
            case "CANCELLED":
                return styles["status-cancelled"];
            default:
                return styles["status-pending"];
        }
    };

    return (
        <SimpleCardContainer className={styles['compensation-card']}>
            <HorizontalContainer justifyContent="space-between" paddingLeft={"10px"} margin={"0px 0px 10px 0px"}>
                <BoldText text={"Solicitudes de compensación"} size="sm" />
            </HorizontalContainer>
            <PaginatedGridTable
                columns={tableColumns}
                items={requests}
                isLoading={loading}
                pageSize={4}
                className={styles["compensation-table"]}
                bodyClassName={styles["compensation-table-body"]}
                emptyState="No hay solicitudes de compensación."
                renderRow={(request) => (
                    <GridTableRow key={request.id}>
                        <GridTableCell>{request.collaboratorName}</GridTableCell>
                        <GridTableCell>{request.day}</GridTableCell>
                        <GridTableCell align="center">{minutesToHoursMinutes(request.requestedMinutes)}</GridTableCell>
                        <GridTableCell align="center">
                            <span className={`${styles["status-badge"]} ${getStatusClassName(request.status)}`}>
                                {compensationStatusLabel[request.status]}
                            </span>
                        </GridTableCell>
                        <GridTableCell align="right">
                            {request.status === "PENDING" ? (
                                <div className={styles["compensation-actions"]}>
                                    <button type="button" className={styles["compensation-action-ghost"]} onClick={() => onReject(request)}>
                                        Rechazar
                                    </button>
                                    <button type="button" className={styles["compensation-action-primary"]} onClick={() => onApprove(request)}>
                                        Aprobar
                                    </button>
                                </div>
                            ) : (
                                <span className={styles["compensation-meta"]}>
                                    {request.reason || "Sin detalle"}
                                </span>
                            )}
                        </GridTableCell>
                    </GridTableRow>
                )}
            />
        </SimpleCardContainer>
    );
};

const HourBankScreen: React.FC<{org: Organization}> = ({org}) => {
    const [summary , setSummary] = useState<HourBankSummary>({});
    const [compensationRequests, setCompensationRequests] = useState<CompensationRequestRow[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate();
    const { showPopup } = usePopup();

    const [open, setOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)

    const [date, setDate] = useState<Date>(null)
    const [dateRange, setDateRange] = useState<[Date, Date] | null>(null)
    const [inputDateMethod, setInputDateMethod] = useState('Unica')

    const tableColumns: TableColumn[] = useMemo(() => ([
        { id: "employee", label: "Colaborador", width: "2fr" },
        { id: "worked", label: "Horas trabajadas", width: "1fr" },
        { id: "expected", label: "Horas esperadas", width: "1fr" },
        { id: "balance", label: "Balance", width: "1fr" },
        { id: "actions", label: "", width: "56px", align: "right" },
    ]), []);

    const filteredRows: HourBankTableRow[] = useMemo(() => {
        if (!summary) {
            return [];
        }

        return Object.entries(summary)
            .filter(([, entry]) => entry.name.toLowerCase().includes(searchQuery.toLowerCase()))
            .map(([userID, entry]) => ({
                userID,
                entry: entry as HourBankSummaryEntry,
            }));
    }, [summary, searchQuery]);

    const filteredCompensationRequests = useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();

        if (!normalizedQuery) {
            return compensationRequests;
        }

        return compensationRequests.filter((request) => {
            return (
                request.collaboratorName.toLowerCase().includes(normalizedQuery) ||
                request.day.toLowerCase().includes(normalizedQuery) ||
                compensationStatusLabel[request.status].toLowerCase().includes(normalizedQuery)
            );
        });
    }, [compensationRequests, searchQuery]);

    const dateFilterLabel = useMemo(() => {
        return formatDateFilterLabel(inputDateMethod, date, dateRange, org.timezone);
    }, [inputDateMethod, date, dateRange, org.timezone]);

    const mapCompensationRequestToRow = (
        request: HourBankCompensationRequest,
    ): CompensationRequestRow => ({
        id: request.id,
        userID: request.userID,
        collaboratorName: summary[request.userID]?.name || "Sin nombre",
        day: request.day,
        requestedMinutes: request.requestedMinutes,
        balanceMinutesAtRequest: request.balanceMinutesAtRequest,
        status: request.status,
        createdAt: request.createdAt,
        updatedAt: request.updatedAt,
        reason: request.approvalData?.reason ?? "",
    });


    useEffect(() => {
        loadScreenData(org.id);
    }, [
        org.id,
        inputDateMethod,
        date?.getTime(),
        dateRange?.[0]?.getTime(),
        dateRange?.[1]?.getTime(),
    ]); 

    useEffect(() => {
        setCompensationRequests((previousRequests) =>
            previousRequests.map((request) => ({
                ...request,
                collaboratorName: summary[request.userID]?.name || request.collaboratorName,
            }))
        );
    }, [summary]);

    //Fetches
    async function fetchHourBankSummary(orgID: string): Promise<HourBankSummary> {
        const hourBankService = Application.shared().coreDependencies.hourBankService

        if ((inputDateMethod === 'Unica' && date === null) || (inputDateMethod === 'Rango' && dateRange === null)) {
            return await hourBankService.getHourBankSummary(orgID)
        }

        const startDate = inputDateMethod === 'Rango' ? 
            dateRange[0].toLocaleDateString('en-ZA', {timeZone: org.timezone}).replace(/\//g, '-') 
            : date.toLocaleDateString('en-ZA', {timeZone: org.timezone}).replace(/\//g, '-')
        const endDate = inputDateMethod === 'Rango' ? 
            dateRange[1].toLocaleDateString('en-ZA', {timeZone: org.timezone}).replace(/\//g, '-') 
            : date.toLocaleDateString('en-ZA', {timeZone: org.timezone}).replace(/\//g, '-')

        return await hourBankService.getHourBankSummaryForDateRange(orgID, startDate, endDate)
    }

    async function fetchCompensationRequests(orgID: string): Promise<CompensationRequestRow[]> {
        const compensationService = Application.shared().coreDependencies.hourBankCompensationService;
        const [pendingRequests, processedRequests] = await Promise.all([
            compensationService.getPendingRequests(orgID),
            compensationService.getProcessedRequests(orgID),
        ]);

        return [...pendingRequests, ...processedRequests]
            .map(mapCompensationRequestToRow)
            .sort((a, b) => {
                const aTimestamp = Math.max(a.updatedAt ?? 0, a.createdAt ?? 0);
                const bTimestamp = Math.max(b.updatedAt ?? 0, b.createdAt ?? 0);
                return bTimestamp - aTimestamp;
            });
    }

    async function loadScreenData(orgID: string) {
        setLoading(true)
        try {
            const [summaryResult, compensationResult] = await Promise.all([
                fetchHourBankSummary(orgID),
                fetchCompensationRequests(orgID),
            ]);

            setSummary(summaryResult)
            setCompensationRequests(compensationResult)
        } catch (error) {
            console.error(error)
            setSummary({})
            setCompensationRequests([])
        } finally {
            setLoading(false)
        }
    }

    //Navigation functions
    function navigateToDetail(userID: string) {
        navigate(`/bancoDeHoras/detalle?id=${userID}`, {state: { name: summary[userID]?.name || 'No especificado'.localized() }});
    }

    
    //State change functions
    const updateDate = (updatedDate) => {
        setDate(updatedDate)
    }

    const updateDateRange = (date1, date2) => {
        let startDate = date1 < date2 ? date1 : date2;
        let endDate = date1 > date2 ? date1 : date2;
    
        let processedStartDate, processedEndDate;
    
        if (startDate && endDate) {
            processedStartDate = startDate
            processedEndDate = endDate
            setDateRange([processedStartDate, processedEndDate])
        } else {
            setDateRange(null)
        }
    }

    const handleApproveCompensationRequest = (request: CompensationRequestRow) => {
        showPopup(() => (
            <CompensationReviewPopup
                request={request}
                action="approve"
                onSubmit={async (currentRequest, description) => {
                    const compensationService = Application.shared().coreDependencies.hourBankCompensationService;
                    await compensationService.approveRequest(currentRequest.id, description);
                    await loadScreenData(org.id);
                }}
            />
        ), true);
    };

    const handleRejectCompensationRequest = (request: CompensationRequestRow) => {
        showPopup(() => (
            <CompensationReviewPopup
                request={request}
                action="reject"
                onSubmit={async (currentRequest, description) => {
                    const compensationService = Application.shared().coreDependencies.hourBankCompensationService;
                    await compensationService.rejectRequest(currentRequest.id, description);
                    await loadScreenData(org.id);
                }}
            />
        ), true);
    };

    return (
        <BareScreen title="Banco de Horas" subtitle={`Organización: ${org.name}`} scrollable={true}>
            <div style={{display: "flex", flex: 1, flexDirection: "column", paddingTop: "30px"}}>

                <div className={styles['action-bar']}>
                    <div className={styles["action-bar-left"]}>
                        <div style={{width: "100%", height: 44}}>
                            <SearchBar placeholder="Buscar colaborador..." onChange={(query) => {
                                setSearchQuery(query.target.value);
                            }} />
                        </div>

                        <div className={styles["action-bar-tools"]}>
                            <div style={{width: 44, height: 44}}>
                                <BigRefreshButton text="" onClick={() => {
                                    loadScreenData(org.id);
                                }} />
                            </div>
                            <span className={styles["date-filter-label"]}>{dateFilterLabel}</span>
                        </div>
                    </div>

                    <div className={styles["action-bar-right"]}>
                        <div style={{height: 50}}>
                            <NormalButton text="Elegir fecha/rango" onClick={(event) => {
                                setAnchorEl(event.currentTarget)
                                setOpen(!open)
                            }}>
                                <CalendarMonthOutlined />
                            </NormalButton>
                        </div>
                        {open && <DatePickerPopperWithRange 
                            inputType={inputDateMethod}
                            updateDate={updateDate} 
                            updateDateRange={updateDateRange}
                            onClickAway={() => {setOpen(false)}}
                            setInputType={setInputDateMethod} 
                            placement="bottom-end"
                            anchorElement={anchorEl} 
                            isOpen={open} 
                            currentPickedDate={date}
                            currentPickedDateRange={dateRange}
                            />
                        }

                    </div>
                </div>


                <BankSummary summary={summary} loading={loading}/>

                <CompensationRequestsPanel
                    requests={filteredCompensationRequests}
                    loading={loading}
                    onApprove={handleApproveCompensationRequest}
                    onReject={handleRejectCompensationRequest}
                />

                <div className={styles['hour-bank-list']}>
                    <SimpleCardContainer className={styles["hour-bank-list-card"]}>
                        <GridTable
                            columns={tableColumns}
                            rowTemplate="2fr 1fr 1fr 1fr 56px"
                            bodyClassName={styles['table-container']}
                            isLoading={loading}
                        >
                            {filteredRows.length ? (
                                filteredRows.map(({ userID, entry }) => (
                                    <GridTableRow key={userID}>
                                        <GridTableCell>
                                            <UserCard name={entry.name} imageUrl={entry.image} />
                                        </GridTableCell>
                                        <GridTableCell>{minutesToHoursMinutes(entry.minutesWorked)}</GridTableCell>
                                        <GridTableCell>{minutesToHoursMinutes(entry.minutesExpected)}</GridTableCell>
                                        <GridTableCell
                                            className={
                                                entry.minutesDifference > 0
                                                    ? styles['balance-positive']
                                                    : entry.minutesDifference < 0
                                                        ? styles['balance-negative']
                                                        : styles['balance-neutral']
                                            }
                                        >
                                            {entry.minutesDifference > 0 ? "+" : entry.minutesDifference < 0 ? "-" : ""}
                                            {minutesToHoursMinutes(Math.abs(entry.minutesDifference))}
                                        </GridTableCell>
                                        <GridTableCell align="right">
                                            <div className={styles['row-button']} onClick={() => navigateToDetail(userID)}>
                                                <VisibilityOutlined/>
                                            </div>
                                        </GridTableCell>
                                    </GridTableRow>
                                ))
                            ) : !loading ? (
                                <div className={styles["table-empty-state"]}>
                                    {searchQuery.trim()
                                        ? "No hay colaboradores que coincidan con la búsqueda."
                                        : "No hay colaboradores para mostrar."}
                                </div>
                            ) : null}
                        </GridTable>
                    </SimpleCardContainer>
                </div>
            </div>
        </BareScreen>
    );
}

export default HourBankScreen;
